import { ColumnDef } from '@tanstack/react-table'
import { isEmpty } from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'urql'
import { FilterValueType, FilterableColumn } from 'app/filter.utils'
import { FileImportJson, ImportJson } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useOrganization } from 'app/utils'
import { logError } from 'lib/logging'
import { fileColumns } from './fileColumns'
import { FILE_IMPORTS_QUERY, IMPORTS_QUERY, DELETE_FILE_IMPORT_MUTATION } from './graphql'

export type ImportsLoaderData = {
  fileImports: Promise<{ hits: FileImportJson[]; total: number }>
}

export type UseImportsReturn = {
  fileImports: { hits: FileImportJson[]; total: number } | undefined
  isLoading: boolean
  customTotalItemsText: string
  columns: ColumnDef<any, any>[]
  filterableColumns: FilterableColumn[]
  isImportedFileOpen: boolean
  shouldDeleteFile: boolean
  selectedFile: FileImportJson | null
  importContent: ImportJson[] | undefined
  isDeleting: boolean
  openImportModal: boolean
  orgId: string | undefined
  fileHandler: (file: FileImportJson) => void
  handleDelete: (file: FileImportJson) => void
  closeFileModal: () => void
  setShouldDeleteFile: (val: boolean) => void
  setOpenImportModal: (val: boolean) => void
  setSearchValue: (val: string | undefined) => void
}

const defaultFilterableColumns: FilterableColumn[] = [
  {
    attribute: `createdDate`,
    displayName: `Import Date`,
    type: FilterValueType.rangeDate,
    defaultValue: [moment().startOf(`week`).format(`YYYY-MM-DD`), moment().endOf(`week`).format(`YYYY-MM-DD`)],
  },
]

export function useImports(): UseImportsReturn {
  const org = useOrganization()

  const [isImportedFileOpen, setIsImportedFileOpen] = useState(false)
  const [shouldDeleteFile, setShouldDeleteFile] = useState(false)
  const [selectedFile, setSelectedFile] = useState<FileImportJson | null>(null)
  const [openImportModal, setOpenImportModal] = useState(false)
  const [searchValue, setSearchValue] = useState<string | undefined>()

  // Fetch file imports via GraphQL. Deletes propagate via graphcache
  // invalidation (see urqlClient updates.Mutation.deleteFileImport), so no
  // manual reexecute handle is needed.
  const [{ data: fileImportsData, fetching: fileImportsFetching, error: fileImportsError }] = useQuery<{
    fileImports: { hits: FileImportJson[]; total: number; page: number }
  }>({
    query: FILE_IMPORTS_QUERY,
    variables: { sort: { field: 'createdDate', direction: 'DESC' } },
    requestPolicy: 'cache-and-network',
  })

  // Fetch imports for selected file via GraphQL
  const importSearchTerm = selectedFile
    ? `fileImportId:${selectedFile.id}${searchValue ? ` AND name:(${searchValue})` : ``}`
    : ``
  const [{ data: importsData, fetching: importsFetching, error: importsError }] = useQuery<{
    imports: { hits: ImportJson[]; total: number; page: number }
  }>({
    query: IMPORTS_QUERY,
    variables: { search: importSearchTerm },
    pause: !selectedFile,
    requestPolicy: 'network-only',
  })

  // Delete file import mutation
  const [{ fetching: isDeleting }, deleteFileImport] = useMutation(DELETE_FILE_IMPORT_MUTATION)

  // Handle errors
  useEffect(() => {
    if (fileImportsError) {
      logError(`Error fetching file imports`, fileImportsError)
    }
    if (importsError) {
      logError(`Error fetching import content`, importsError)
    }
  }, [fileImportsError, importsError])

  const fileImports = fileImportsData?.fileImports
    ? { hits: fileImportsData.fileImports.hits, total: fileImportsData.fileImports.total }
    : undefined

  const importContent = importsData?.imports?.hits

  const customTotalItemsText = `${fileImports?.total.toLocaleString(`en-US`) ?? 0} ${
    (fileImports?.total || 0) > 1 ? `imports` : `import`
  }`

  const closeFileModal = () => {
    setSelectedFile(null)
    setIsImportedFileOpen(false)
  }

  const fileHandler = (file: FileImportJson) => {
    setSelectedFile(file)
    setIsImportedFileOpen(true)
  }

  const handleDelete = useCallback(
    async (file: FileImportJson) => {
      if (!file.id) return
      try {
        const result = await deleteFileImport({ id: file.id })
        if (result.error) {
          throw result.error
        }
        if (result.data?.deleteFileImport) {
          notifySuccess(`Import deleted successfully`)
          closeFileModal()
          // No manual refetch: urqlClient's updates.Mutation.deleteFileImport
          // invalidates FileImport:<id>, so the cache-and-network fileImports
          // list query re-executes and the row drops automatically.
        } else {
          notifyError(`Error deleting import`)
        }
      } catch (err) {
        logError(`Error deleting import`, err)
        notifyError(`Error deleting import`)
      } finally {
        setShouldDeleteFile(false)
      }
    },
    [deleteFileImport],
  )

  const columns = useMemo(() => fileColumns(fileHandler), [])

  return {
    fileImports,
    isLoading: fileImportsFetching,
    customTotalItemsText,
    columns,
    filterableColumns: defaultFilterableColumns,
    isImportedFileOpen,
    shouldDeleteFile,
    selectedFile,
    importContent,
    isDeleting,
    openImportModal,
    orgId: org?.id,
    fileHandler,
    handleDelete,
    closeFileModal,
    setShouldDeleteFile,
    setOpenImportModal,
    setSearchValue,
  }
}
